// src/features/tag/TagManageUtils.ts

import { TAG_MANAGE } from '@/lib/constants/tag';
import type { TagUsage } from '@/lib/types/Tag';

/** Minimal playlist shape needed for smart-playlist warnings. */
export interface TagRulePlaylistRef {
  listId: number;
  title: string;
  tagRules: { tag: string; rule: string }[];
}

/**
 * Pure decision logic for the Manage Tags drawer. All predicates the UI
 * gates destructive actions on live here so they are unit-testable.
 */
export class TagManageUtils {

  /** Total media references of any type. */
  static totalMediaCount(u: TagUsage): number {
    return u.videoCount + u.streamCount + u.otherMediaCount;
  }

  /** Unused = safe to delete with a light confirm. */
  static isUnused(u: TagUsage): boolean {
    return this.totalMediaCount(u) === 0 && u.listCount === 0
      && u.scheduleCount === 0 && u.ingestCfgCount === 0;
  }

  /** Large cascades require typing the tag name to confirm. */
  static needsTypedConfirm(u: TagUsage): boolean {
    return this.totalMediaCount(u) >= TAG_MANAGE.DELETE_TYPED_CONFIRM_THRESHOLD;
  }

  /**
   * Case-insensitive substring filter + optional unused-only narrowing.
   * Input order (server: label asc) is preserved.
   */
  static filterUsages(usages: TagUsage[], search: string, unusedOnly: boolean): TagUsage[] {
    const term = search.trim().toLowerCase();
    return usages.filter((u) => {
      if (unusedOnly && !this.isUnused(u)) return false;
      if (term.length > 0 && !u.label.toLowerCase().includes(term)) return false;
      return true;
    });
  }

  /**
   * Smart playlists whose tag rules reference the label. Rule views carry the
   * CURRENT label (the backend joins tag at read time), so label matching is
   * exact; case-insensitive to tolerate legacy-era casing variants.
   */
  static smartPlaylistRefs(playlists: TagRulePlaylistRef[], label: string): TagRulePlaylistRef[] {
    const lower = label.toLowerCase();
    return playlists.filter((p) => p.tagRules.some((r) => r.tag.toLowerCase() === lower));
  }

  /**
   * Rename validation shared by the form schema and submit guard.
   * Returns an error message or null. Recasing the same tag is allowed;
   * colliding with a DIFFERENT tag's label is not (merge is a future op).
   */
  static validateRenameLabel(rawLabel: string, usages: TagUsage[], currentTagId: number): string | null {
    const label = rawLabel.trim();
    if (label.length === 0) return 'Tag name is required';
    if (label.length > TAG_MANAGE.LABEL_MAX_LENGTH) {
      return `Tag name must be at most ${TAG_MANAGE.LABEL_MAX_LENGTH} characters`;
    }
    const lower = label.toLowerCase();
    const conflict = usages.find((u) => u.tagId !== currentTagId && u.label.toLowerCase() === lower);
    if (conflict) return `A tag named "${conflict.label}" already exists`;
    return null;
  }

  /**
   * "3 videos · 1 stream · 2 record schedules · 1 upload config" — zero parts
   * omitted; 'Unused' when nothing (playlist refs render as a badge, not here).
   */
  static formatUsage(u: TagUsage): string {
    const parts: string[] = [];
    if (u.videoCount > 0) parts.push(`${u.videoCount} video${u.videoCount === 1 ? '' : 's'}`);
    if (u.streamCount > 0) parts.push(`${u.streamCount} stream${u.streamCount === 1 ? '' : 's'}`);
    if (u.otherMediaCount > 0) parts.push(`${u.otherMediaCount} other media`);
    if (u.scheduleCount > 0) parts.push(`${u.scheduleCount} record schedule${u.scheduleCount === 1 ? '' : 's'}`);
    if (u.ingestCfgCount > 0) parts.push(`${u.ingestCfgCount} upload config${u.ingestCfgCount === 1 ? '' : 's'}`);
    if (parts.length === 0 && u.listCount === 0) return 'Unused';
    return parts.join(' · ');
  }
}
